import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaTrash, FaEdit, FaCheck, FaTimes, FaShoppingBag, FaExpand, FaInfo, FaVideo } from 'react-icons/fa';
import {
  getAllOrdersAsync,
  acceptCartAsync,
  makeOrderAsync,
  updateCartItemAsync,
  removeCartItemAsync
} from '../../features/order/orderSlice';
import { fetchProductAsync } from '../../features/products/productsSlice';
import RetailerNavbar from '../../features/retailer/components/RetailerNavbar';
import Pagination from '../../features/components/Pagination';
import { toast } from 'react-toastify';
import Footer from '../../features/components/Footer';

const ITEMS_PER_PAGE = 10;

const ProductDetailsDialog = ({ isOpen, onClose, product }) => {
  const [selectedMedia, setSelectedMedia] = useState('cover_image');

  if (!isOpen || !product) return null;

  const {
    name,
    weight,
    purity,
    making_charge: making,
    cover_image,
    image2,
    image3,
    video,
    quantity,
    registered_firm_name: registeredFirmName,
    description,
    category,
    owner_name: ownerName
  } = product;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold text-gray-900">{name}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes className="h-6 w-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Media Section */}
            <div className="space-y-4">
              {/* Main Media Display */}
              <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-200">
                {selectedMedia === 'video' && video ? (
                  <video
                    src={video}
                    controls
                    className="h-full w-full object-cover object-center"
                  />
                ) : (
                  <img
                    src={selectedMedia === 'cover_image' ? cover_image :
                        selectedMedia === 'image2' ? image2 :
                        selectedMedia === 'image3' ? image3 : cover_image}
                    alt={name}
                    className="h-full w-full object-cover object-center"
                  />
                )}
              </div>

              {/* Thumbnail Gallery */}
              <div className="grid grid-cols-4 gap-2">
                <div
                  className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                    selectedMedia === 'cover_image' ? 'ring-2 ring-yellow-500' : ''
                  }`}
                  onClick={() => setSelectedMedia('cover_image')}
                >
                  <img
                    src={cover_image}
                    alt="Cover"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                
                {image2 && (
                  <div
                    className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                      selectedMedia === 'image2' ? 'ring-2 ring-yellow-500' : ''
                    }`}
                    onClick={() => setSelectedMedia('image2')}
                  >
                    <img
                      src={image2}
                      alt="Image 2"
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                )}

                {image3 && (
                  <div
                    className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                      selectedMedia === 'image3' ? 'ring-2 ring-yellow-500' : ''
                    }`}
                    onClick={() => setSelectedMedia('image3')}
                  >
                    <img
                      src={image3}
                      alt="Image 3"
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                )}

                {video && (
                  <div
                    className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer relative ${
                      selectedMedia === 'video' ? 'ring-2 ring-yellow-500' : ''
                    }`}
                    onClick={() => setSelectedMedia('video')}
                  >
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                      <FaVideo className="text-white text-3xl" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Details Section */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {weight && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Weight</h3>
                      <p className="mt-1 text-sm text-gray-900">{weight} g</p>
                    </div>
                  )}
                  {purity && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Purity</h3>
                      <p className="mt-1 text-sm text-gray-900">{purity}K</p>
                    </div>
                  )}
                  {making && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Making Charge</h3>
                      <p className="mt-1 text-sm text-gray-900">{making}%</p>
                    </div>
                  )}
                  {quantity && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Available Quantity</h3>
                      <p className="mt-1 text-sm text-gray-900">{quantity}</p>
                    </div>
                  )}
                </div>

                {category && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Category</h3>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{category}</p>
                  </div>
                )}

                {description && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Description</h3>
                    <p className="mt-1 text-sm text-gray-900">{description}</p>
                  </div>
                )}

                {(ownerName || registeredFirmName) && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Seller Details</h3>
                    <div className="mt-2 space-y-2">
                      {ownerName && (
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">Name:</span> {ownerName}
                        </p>
                      )}
                      {registeredFirmName && (
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">Firm:</span> {registeredFirmName}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CartItem = ({ item, onDelete, onOrder, onEditQuantity, isEditing, onSaveEdit, onInfoClick }) => {
  const [editedQuantity, setEditedQuantity] = useState(item.quantity);
  const [isImageExpanded, setIsImageExpanded] = useState(false);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0) {
      setEditedQuantity(value);
    }
  };

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <img
              src={item.cover_image}
              alt={item.product_name}
              className={`h-12 w-12 object-cover rounded-md cursor-pointer transition-all duration-300 ${
                isImageExpanded ? 'h-48 w-48' : ''
              }`}
              onClick={() => setIsImageExpanded(!isImageExpanded)}
            />
            <FaExpand className="absolute bottom-1 right-1 text-gray-500 text-xs" />
          </div>
          <div>
            <div className="font-medium">{item.product_name}</div>
            <div className="text-gray-500 text-xs">{item.registered_firm_name}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {isEditing ? (
          <input
            type="number"
            value={editedQuantity}
            onChange={handleQuantityChange}
            min="1"
            className="w-20 px-2 py-1 border rounded-md"
          />
        ) : (
          item.quantity
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {new Date(item.order_date).toLocaleDateString()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex justify-end space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={() => onSaveEdit(item.order_id, editedQuantity)}
                className="text-green-600 hover:text-green-900"
                title="Save"
              >
                <FaCheck className="h-5 w-5" />
              </button>
              <button
                onClick={() => onEditQuantity(null)}
                className="text-red-600 hover:text-red-900"
                title="Cancel"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onInfoClick(item.product_id)}
                className="text-blue-600 hover:text-blue-900"
                title="View Details"
              >
                <FaInfo className="h-5 w-5" />
              </button>
              <button
                onClick={() => onEditQuantity(item.order_id)}
                className="text-blue-600 hover:text-blue-900"
                title="Edit Quantity"
              >
                <FaEdit className="h-5 w-5" />
              </button>
              <button
                onClick={() => onOrder(item.order_id)}
                className="text-yellow-600 hover:text-yellow-900"
                title="Move to Orders"
              >
                <FaShoppingBag className="h-5 w-5" />
              </button>
              <button
                onClick={() => onDelete(item.order_id)}
                className="text-red-600 hover:text-red-900"
                title="Remove from Cart"
              >
                <FaTrash className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

const RetailerCart = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [editingItemId, setEditingItemId] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const { cartOrders: cart, loading, error } = useSelector(state => state.orders);
  const { currentProduct } = useSelector(state => state.products);

  useEffect(() => {
    dispatch(getAllOrdersAsync('CART'));
  }, [dispatch]);

  useEffect(() => {
    if (selectedProductId) {
      dispatch(fetchProductAsync(selectedProductId));
    }
  }, [dispatch, selectedProductId]);

  const handleUpdateQuantity = async (orderId, newQuantity) => {
    try {
      await dispatch(updateCartItemAsync({ orderId, quantity: newQuantity })).unwrap();
      setEditingItemId(null);
      toast.success('Cart updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update cart');
    }
  };

  const handleRemoveFromCart = async (orderId) => {
    try {
      await dispatch(removeCartItemAsync(orderId)).unwrap();
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error(error.message || 'Failed to remove item from cart');
    }
  };

  const handleMoveToOrders = async (orderId) => {
    try {
      await dispatch(acceptCartAsync(orderId)).unwrap();
      toast.success('Item moved to orders successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to move item to orders');
    }
  };

  const handleInfoClick = (productId) => {
    setSelectedProductId(productId);
  };

  const handleCloseDialog = () => {
    setSelectedProductId(null);
  };

  const paginatedCart = cart.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(cart.length / ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50">
      <RetailerNavbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-20">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Shopping Cart</h1>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 p-4 rounded-md mb-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && cart.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Your cart is empty</p>
            </div>
          )}

          {!loading && !error && cart.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedCart.map((item) => (
                      <CartItem
                        key={item.order_id}
                        item={item}
                        onDelete={handleRemoveFromCart}
                        onOrder={handleMoveToOrders}
                        onEditQuantity={setEditingItemId}
                        isEditing={editingItemId === item.order_id}
                        onSaveEdit={handleUpdateQuantity}
                        onInfoClick={handleInfoClick}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ProductDetailsDialog
        isOpen={!!selectedProductId}
        onClose={handleCloseDialog}
        product={currentProduct}
      />
      <Footer />
    </div>
  );
};

export default RetailerCart;
